import type { SequenceFormatValue, SequenceTypeValue } from '@my/directory-api';
import { InlineEditor, Switch, TextInput } from '@my/ui';
import { Lock } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * One sequence's worth of editable fields. `currentNumber` is held as a
 * string for stable input handling — parsed back to a number on submit.
 * `format` controls the reference shape: `Plain` uses the prefix verbatim,
 * `YearMonth` renders `YYMM-` and ignores the prefix on render.
 */
export interface SequenceFormFields {
  format: SequenceFormatValue;
  prefix: string;
  currentNumber: string;
  isLocked: boolean;
}

/**
 * Sequence types the editor surfaces, in display order. Currently a
 * subset of the BE enum (Purchase Order / Delivery Note / Receipt are
 * deliberately omitted from the Organisation page — they may move to
 * their own dedicated screens in the future). Adding a type here is
 * the single source of truth: the form record, the empty-defaults
 * builder, the save loop, and the renderer all read from this const.
 */
export const EDITOR_SEQUENCE_TYPES = [
  'Invoice',
  'CreditNote',
  'Proforma',
  'Order',
  'Manifest',
] as const satisfies readonly SequenceTypeValue[];

/** Element type of `EDITOR_SEQUENCE_TYPES` — the sequence types the editor handles. */
export type EditableSequenceType = (typeof EDITOR_SEQUENCE_TYPES)[number];

/**
 * Default prefix per type — applied when no `OrganisationSequence` row
 * exists yet on the BE so the editor renders sensible starter values.
 * Operators can edit before saving; rows the operator never touches stay
 * unsaved (see `touched` tracking in `OrganisationPage`).
 */
export const DEFAULT_PREFIX_BY_TYPE: Record<SequenceTypeValue, string> = {
  Invoice: 'INV',
  CreditNote: 'CN',
  Proforma: 'PRO',
  PurchaseOrder: 'PO',
  Order: 'ORD',
  Manifest: 'MAN',
  DeliveryNote: 'DN',
  Receipt: 'RCT',
};

export type SequenceConfigFormValue = Record<EditableSequenceType, SequenceFormFields>;

export interface SequenceConfigEditorProps {
  value: SequenceConfigFormValue;
  onChange: (type: EditableSequenceType, next: SequenceFormFields) => void;
  /**
   * Server-side `isLocked` per type. Locked rows render their
   * `currentNumber` read-only and hide the "lock on save" tick — the
   * BE rejects unlock attempts as 409 Conflict, so the FE never offers
   * the affordance.
   */
  serverLocked: Record<SequenceTypeValue, boolean>;
  /**
   * `true` while the sequence list hasn't returned yet. Disables the
   * Lock checkbox so a first-paint click can't accidentally seal a row
   * the operator hasn't actually configured.
   */
  loading?: boolean;
}

/**
 * Editor for all `OrganisationSequence` rows on an organisation. One
 * panel covers the seven document types — Invoice / Credit Note /
 * Purchase Order / Order / Manifest / Delivery Note / Receipt — each
 * mapping to a single `OrganisationSequence` row on the BE.
 *
 * <p>
 * The "lock on save" checkbox is part of form state; the actual lock
 * transition fires as part of the page's main save flow, not a
 * separate mutation, so a single green-tick commit covers everything
 * on the page.
 * </p>
 */
export function SequenceConfigEditor({
  value,
  onChange,
  serverLocked,
  loading,
}: SequenceConfigEditorProps) {
  const { t } = useTranslation('organisation');

  // Summary line shows the next-number preview for the three most
  // operationally important sequences (Invoice / Credit Note / Order)
  // — fitting all rows in the InlineEditor header would crowd it. The
  // editor body lets the operator see every row.
  const summary = useMemo(() => {
    const previewTypes: EditableSequenceType[] = ['Invoice', 'CreditNote', 'Order'];
    return previewTypes
      .map((type) => renderPreview(value[type], DEFAULT_PREFIX_BY_TYPE[type]))
      .join(' · ');
  }, [value]);

  return (
    <InlineEditor label={t('sequenceConfig.title')} summary={summary}>
      {/* Generous gap between rows so the three-input grid + helper +
          lock checkbox of one row doesn't visually run into the next.
          `divide-y` adds a faint rule so the eye lands cleanly on each
          row's title even at full panel height. */}
      <div className="divide-y divide-slate-300">
        {EDITOR_SEQUENCE_TYPES.map((type, i) => (
          <div key={type} className={i === 0 ? 'pb-4' : 'py-4 last:pb-0'}>
            <SequenceFields
              type={type}
              value={value[type]}
              onChange={(next) => onChange(type, next)}
              serverLocked={serverLocked[type] ?? false}
              loading={loading}
            />
          </div>
        ))}
      </div>
    </InlineEditor>
  );
}

interface SequenceFieldsProps {
  type: EditableSequenceType;
  value: SequenceFormFields;
  onChange: (next: SequenceFormFields) => void;
  serverLocked: boolean;
  loading?: boolean;
}

function SequenceFields({ type, value, onChange, serverLocked, loading }: SequenceFieldsProps) {
  const { t } = useTranslation('organisation');
  const slug = TYPE_SLUG[type];
  const numberDisabled = serverLocked;
  const isYearMonth = value.format === 'YearMonth';
  const typeLabel = t(`sequenceConfig.types.${type}`);
  const fallback = DEFAULT_PREFIX_BY_TYPE[type];

  return (
    <div className="space-y-2">
      <p className="flex items-center gap-1 text-xs font-medium text-slate-700">
        {typeLabel}
        {serverLocked && (
          <span
            className="inline-flex items-center gap-1 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
            aria-label={t('sequenceConfig.lockedBadge')}
          >
            <Lock className="h-3 w-3" />
            {t('sequenceConfig.lockedBadge')}
          </span>
        )}
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {/* Format toggle — Plain (off) ↔ Year/Month (on). The live
            preview line below shows the operator exactly what the
            picked format will mint, so we don't repeat the example
            shape in the toggle's label any more. */}
        <Switch
          label={t('sequenceConfig.format.label')}
          name={`${slug}-format`}
          checked={isYearMonth}
          disabledLabel={t('sequenceConfig.format.plain.label')}
          enabledLabel={t('sequenceConfig.format.yearMonth.label')}
          onChange={(e) =>
            onChange({
              ...value,
              format: (e.target.checked ? 'YearMonth' : 'Plain') as SequenceFormatValue,
            })
          }
        />
        <TextInput
          label={t('sequenceConfig.prefixLabel', { type: typeLabel })}
          name={`${slug}-prefix`}
          autoComplete="off"
          value={isYearMonth ? '' : value.prefix}
          maxLength={16}
          disabled={isYearMonth}
          placeholder={isYearMonth ? t('sequenceConfig.format.yearMonth.prefixPlaceholder') : ''}
          onChange={(e) => onChange({ ...value, prefix: e.target.value.toUpperCase() })}
          //helperText={isYearMonth ? t('sequenceConfig.format.yearMonth.prefixHelper') : undefined}
        />
        <TextInput
          label={t('sequenceConfig.startingNumber')}
          name={`${slug}-current-number`}
          autoComplete="off"
          inputMode="numeric"
          value={value.currentNumber}
          disabled={numberDisabled}
          onChange={(e) =>
            onChange({ ...value, currentNumber: e.target.value.replace(/[^\d]/g, '') })
          }
        />
      </div>
      <p className="text-xs italic text-slate-500">
        {isYearMonth ? t('sequenceConfig.format.yearMonth.prefixHelper') : undefined}
      </p>
      <p className="text-xs italic text-slate-500">
        {serverLocked ? t('sequenceConfig.lockedHelper') : t('sequenceConfig.editableHelper')}
      </p>
      <p className="text-xs text-slate-500">
        {t('sequenceConfig.previewLabel')}:{' '}
        <span className="font-mono font-medium text-slate-700">
          {renderPreview(value, fallback)}
        </span>
      </p>
      {!serverLocked && (
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={value.isLocked}
            disabled={loading}
            onChange={(e) => onChange({ ...value, isLocked: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-slate-300"
          />
          <span>{t('sequenceConfig.lockMessage', { type: typeLabel })}</span>
        </label>
      )}
    </div>
  );
}

/**
 * Stable slugs for input `name` attributes — kebab-case derived from
 * the BE enum string. Centralised so adding a new type only requires
 * one entry instead of three (label + slug + lookup).
 */
const TYPE_SLUG: Record<SequenceTypeValue, string> = {
  Invoice: 'invoice',
  CreditNote: 'credit-note',
  Proforma: 'proforma',
  PurchaseOrder: 'purchase-order',
  Order: 'order',
  Manifest: 'manifest',
  DeliveryNote: 'delivery-note',
  Receipt: 'receipt',
};

/**
 * Render the live preview string the editor surfaces in two places (inline
 * summary + per-row helper). Plain uses the typed prefix; YearMonth uses
 * the current UTC year/month so the operator sees what next month's first
 * number will look like as they pick.
 */
function renderPreview(fields: SequenceFormFields, fallbackPrefix: string): string {
  const number = (fields.currentNumber || '1').padStart(6, '0');
  if (fields.format === 'YearMonth') {
    const now = new Date();
    const yy = String(now.getUTCFullYear() % 100).padStart(2, '0');
    const mm = String(now.getUTCMonth() + 1).padStart(2, '0');
    return `${yy}${mm}-${number}`;
  }
  return `${fields.prefix || fallbackPrefix}${number}`;
}
